import { useQuery } from '@apollo/client';
import { useMemo, useCallback, useState } from 'react';
import { gql } from 'graphql-tag';
import { DateTime } from 'luxon';
import { getTodayForUser } from '~/modules/common/dates/today';
import { useMeContext } from '~/modules/me';
import { SortDirection } from '~/types';

export const GET_PAGE_OF_PROJECT_COST_RATES = gql`
  query getPageOfProjectCostRates(
    $projectId: String!
    $pageSize: Int!
    $page: Int!
    $dimensionUris: [String!]
    $filters: ProjectRateFilter
    $projectCostRateCountFilters: ProjectRateFilter
    $sort: [ProjectRateSort]
  ) {
    project(projectId: $projectId) {
      id
      projectCostRateCount(filters: $projectCostRateCountFilters)
      allProjectCostRatesCount: projectCostRateCount(filters: $filters)
      pageOfProjectCostRate(
        page: $page
        pageSize: $pageSize
        filters: $filters
        dimensionUris: $dimensionUris
        sort: $sort
      ) {
        id
        dimensions {
          dimensionUri
          dimensionValueReference {
            id
            displayText
          }
        }
        effectiveDate
        percentMarkup
        amount {
          amount
          currency {
            id
            displayText
            symbol
          }
        }
      }
    }
  }
`;

const FULL_PAGE_SIZE = 1000;

const getToday = me => {
  const { day, month, year } = getTodayForUser(me);

  return DateTime.utc(year, month, day);
};

export const mapToGetPageOfProjectCostRatesVariables = (
  projectId,
  projectCostRateDimensionUris,
  me
) => {
  const today = getToday(me);

  return {
    projectId,
    dimensionUris: projectCostRateDimensionUris,
    sort: (projectCostRateDimensionUris || []).map(dimensionUri => ({
      dimensionUri,
      direction: SortDirection.Asc
    })),
    page: 1,
    pageSize: 25,
    projectCostRateCountFilters: {
      effectiveDateRange: { startDate: today, endDate: today }
    }
  };
};

const areAllDimensionsPresentInPrevRates = (
  prevRateDimensions,
  currDimensionsUrisSet
) =>
  (prevRateDimensions || []).every(obj =>
    currDimensionsUrisSet.has(obj.dimensionUri)
  );

export const getModifiedRates = rates =>
  rates.reduce((acc, rate, index) => {
    if (rate.dimensions === null) {
      return [...acc, { ...rate, isDimensionDeleted: false }];
    }

    const isDimensionDeleted =
      rate.dimensions.at(-1)?.dimensionValueReference?.displayText ===
        'OBJECT_DELETED' ||
      (index > 0 &&
        areAllDimensionsPresentInPrevRates(
          rates[index - 1].dimensions,
          new Set(rate.dimensions.map(d => d.dimensionUri))
        ) &&
        acc[index - 1].isDimensionDeleted);

    return [...acc, { ...rate, isDimensionDeleted }];
  }, []);

export const useProjectCostOverridesData = ({
  projectId,
  dimensionUris,
  skip = false
} = {}) => {
  const me = useMeContext();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { data, loading, fetchMore, refetch, variables } = useQuery(
    GET_PAGE_OF_PROJECT_COST_RATES,
    {
      variables: mapToGetPageOfProjectCostRatesVariables(
        projectId,
        dimensionUris,
        me
      ),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: skip || !projectId
    }
  );

  const rates = useMemo(() => data?.project?.pageOfProjectCostRate || [], [
    data?.project?.pageOfProjectCostRate
  ]);
  const groupCount = data?.project?.projectCostRateCount || 0;
  const totalCount = data?.project?.allProjectCostRatesCount ?? null;

  const hasMore = totalCount > rates?.length;

  const loadMore = useCallback(
    async onAddEntryClick => {
      if (!hasMore) return;
      setIsLoadingMore(true);
      try {
        await fetchMore({
          query: GET_PAGE_OF_PROJECT_COST_RATES,
          variables: {
            ...variables,
            page: onAddEntryClick
              ? 1
              : Math.floor(rates.length / variables.pageSize) + 1,
            pageSize: onAddEntryClick ? FULL_PAGE_SIZE : variables.pageSize
          },
          updateQuery: (
            previousResult,
            {
              fetchMoreResult: {
                project: { pageOfProjectCostRate: fetchMoreResult }
              }
            }
          ) => ({
            project: {
              ...previousResult.project,
              pageOfProjectCostRate: onAddEntryClick
                ? [...fetchMoreResult]
                : [...rates, ...fetchMoreResult]
            }
          })
        });
      } finally {
        setIsLoadingMore(false);
      }
    },
    [fetchMore, hasMore, rates, variables]
  );

  const reloadAll = useCallback(async () => {
    const result = await refetch({ page: 1, pageSize: FULL_PAGE_SIZE });

    return getModifiedRates(result?.data?.project?.pageOfProjectCostRate || []);
  }, [refetch]);

  const projectRates = useMemo(() => getModifiedRates(rates), [rates]);

  return {
    loading: loading && !data,
    projectRates,
    projectRateCount: groupCount,
    hasMore,
    loadingMore: isLoadingMore,
    loadMore,
    reloadAll
  };
};

export default useProjectCostOverridesData;
